import { Youtube, Instagram, MessageCircle, ArrowUpRight } from "lucide-react";

const AppleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.34 0A5.328 5.328 0 000 5.34v13.32A5.328 5.328 0 005.34 24h13.32A5.328 5.328 0 0024 18.66V5.34A5.328 5.328 0 0018.66 0H5.34zm6.525 2.568c2.336 0 4.448.902 4.448 3.72 0 1.867-.78 3.14-2.336 3.14-.998 0-1.867-.586-1.867-1.563 0-1.563 1.563-2.14 3.43-2.14v-.293c0-1.172-.878-1.758-2.043-1.758-1.172 0-2.336.586-3.14 1.172l-.586-1.172c1.172-.781 2.531-1.106 4.094-1.106zm-2.238 11.064c1.367 0 2.531.781 2.531 2.238 0 .098 0 .195-.098.39l-.879 4.005c-.195.781-.683 1.367-1.562 1.367-.88 0-1.368-.586-1.563-1.367l-.879-4.004c0-.196-.097-.293-.097-.391 0-1.457 1.172-2.238 2.547-2.238z" />
  </svg>
);

const SpotifyIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: "YouTube", icon: Youtube, url: "https://www.youtube.com/@TruthsChaosCupcakes" },
    { name: "Apple", icon: AppleIcon, url: "https://podcasts.apple.com/us/podcast/truths-chaos-cupcakes/id1863379051" },
    { name: "Spotify", icon: SpotifyIcon, url: "https://open.spotify.com/show/21uuyFWypu8Jdv2GeOsgZ0" },
    { name: "Instagram", icon: Instagram, url: "https://www.instagram.com/truthschaoscupcakes/" },
    { name: "WhatsApp", icon: MessageCircle, url: "https://wa.me/34632628051?text=Hi!%20I%20want%20to%20join%20the%20TCC%20community" },
  ];

  const footerLinks = [
    { label: "Episodes", href: "#episodes" },
    { label: "About", href: "#about" },
    { label: "Be a Guest", href: "#be-a-guest" },
    { label: "Subscribe", href: "#newsletter" },
  ];

  return (
    <footer className="border-t border-[#232332] pt-14 pb-8 px-5 sm:px-8 lg:px-12" data-testid="footer">
      {/* Giant wordmark — wraps safely on small screens */}
      <a href="/" className="block mb-14 group" data-testid="footer-logo">
        <span className="font-display text-[clamp(2.2rem,8.5vw,8rem)] leading-[0.9] text-stroke group-hover:text-stroke-pink transition-colors duration-300 block break-words">
          Truths, Chaos &amp; Cupcakes
        </span>
      </a>

      <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12 mb-14">
        {/* Brand */}
        <div className="lg:col-span-5">
          <span className="font-display text-3xl text-[#F4F2EE]">
            TCC<span className="text-[#FF3E8A]">.</span>
          </span>
          <p className="text-[#9494A6] font-light text-sm max-w-xs mt-4 leading-relaxed">
            Real conversations about work, sobriety, community, and building life without a manual.
          </p>
          <div className="flex gap-3 mt-8">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 border border-[#323248] hover:border-[#C8FF3E] hover:bg-[#C8FF3E] hover:text-[#08080D] text-[#F4F2EE] flex items-center justify-center transition-colors duration-150"
                aria-label={social.name}
                data-testid={`social-${social.name.toLowerCase()}`}
              >
                <social.icon size={18} />
              </a>
            ))}
          </div>
        </div>

        {/* Quick links */}
        <div className="lg:col-span-3">
          <p className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#5E5E72] mb-6">Index</p>
          <nav className="space-y-4">
            {footerLinks.map((link, i) => (
              <a
                key={link.label}
                href={link.href}
                className="flex items-baseline gap-3 group w-fit"
                data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="font-mono-tcc text-[10px] text-[#C8FF3E]">0{i + 1}</span>
                <span className="font-mono-tcc text-xs uppercase tracking-[0.15em] text-[#9494A6] group-hover:text-[#F4F2EE] transition-colors duration-150">
                  {link.label}
                </span>
              </a>
            ))}
          </nav>
        </div>

        {/* Savi connection */}
        <div className="lg:col-span-4">
          <p className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#5E5E72] mb-6">
            A production of
          </p>
          <a
            href="https://savicoliving.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between gap-4 border border-[#323248] hover:border-[#FF3E8A] p-6 transition-colors duration-150"
            data-testid="footer-savi-link"
          >
            <div className="min-w-0">
              <p className="font-display text-2xl text-[#FF3E8A]">Savi Coliving</p>
              <p className="text-sm text-[#9494A6] font-light mt-1">
                Intentional living in Valencia, Spain
              </p>
            </div>
            <ArrowUpRight size={20} className="text-[#C8FF3E] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200 shrink-0" />
          </a>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="pt-8 border-t border-[#232332] flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <p className="font-mono-tcc text-[10px] uppercase tracking-[0.2em] text-[#5E5E72]">
          © {currentYear} Truths, Chaos &amp; Cupcakes — All rights reserved
        </p>
        <p className="font-mono-tcc text-[10px] uppercase tracking-[0.2em] text-[#5E5E72]">
          Made in the mountains of Valencia — 39.6°N
        </p>
      </div>
    </footer>
  );
};
